import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Divider,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LockIcon from '@mui/icons-material/Lock';
import { useGame } from '../../contexts/GameContext';
import achievementsData from '../../game/achievements.json';

// ─── Helpers ────────────────────────────────────────────────────────────────

const CATEGORY_COLORS = {
  first_steps:   { bg: '#e8f5e9', border: '#a5d6a7', text: '#2e7d32' },
  streak:        { bg: '#fff3e0', border: '#ffcc80', text: '#e65100' },
  precision:     { bg: '#e3f2fd', border: '#90caf9', text: '#1565c0' },
  explorer:      { bg: '#f3e5f5', border: '#ce93d8', text: '#6a1b9a' },
  persistence:   { bg: '#fce4ec', border: '#f48fb1', text: '#880e4f' },
  completionist: { bg: '#fffde7', border: '#fff176', text: '#f57f17' },
};

const TIER_LABELS = { 1: 'Bronze', 2: 'Silver', 3: 'Gold', 4: 'Platinum' };
const TIER_COLORS = {
  1: '#cd7f32',
  2: '#9e9e9e',
  3: '#ffc107',
  4: '#00bcd4',
};

// ─── Single Achievement Card ─────────────────────────────────────────────────

const AchievementCard = ({ achievement, unlockedEntry, progressInfo }) => {
  const { id, category, icon, title, description, hint, tier, progressMax } = achievement;
  const unlocked = !!unlockedEntry;
  const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS.first_steps;

  const unlockedDate = unlockedEntry?.unlockedAt
    ? new Date(unlockedEntry.unlockedAt).toLocaleDateString(undefined, {
        month: 'short', day: 'numeric', year: 'numeric',
      })
    : null;

  // Near-miss display: show progress bar when ≥ 20 % of the way there
  const showProgress = !unlocked && progressInfo && progressInfo.pct >= 20;

  return (
    <Card
      elevation={unlocked ? 3 : 1}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        border: unlocked
          ? `2px solid ${colors.border}`
          : '2px solid #e0e0e0',
        background: unlocked ? colors.bg : '#fafafa',
        opacity: unlocked ? 1 : 0.75,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-3px)',
          boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
          opacity: 1,
        },
        position: 'relative',
        overflow: 'visible',
      }}
    >
      {/* Tier badge */}
      {tier && (
        <Box
          sx={{
            position: 'absolute',
            top: -8,
            right: 10,
            bgcolor: TIER_COLORS[tier],
            color: tier === 2 ? '#fff' : '#fff',
            fontSize: '0.65rem',
            fontWeight: 'bold',
            px: 1,
            py: 0.25,
            borderRadius: 1,
            boxShadow: 1,
          }}
        >
          {TIER_LABELS[tier]}
        </Box>
      )}

      <CardContent sx={{ flexGrow: 1, p: 2 }}>
        {/* Icon + lock overlay */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              fontSize: '2rem',
              lineHeight: 1,
              filter: unlocked ? 'none' : 'grayscale(1)',
              flexShrink: 0,
              position: 'relative',
            }}
          >
            {icon}
            {!unlocked && (
              <LockIcon
                sx={{
                  position: 'absolute',
                  bottom: -4,
                  right: -6,
                  fontSize: 14,
                  color: '#9e9e9e',
                  bgcolor: '#fff',
                  borderRadius: '50%',
                }}
              />
            )}
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 'bold',
                color: unlocked ? colors.text : '#757575',
                lineHeight: 1.2,
                mb: 0.3,
              }}
            >
              {unlocked ? title : '???'}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: unlocked ? '#555' : '#9e9e9e',
                display: 'block',
                lineHeight: 1.4,
              }}
            >
              {unlocked ? description : hint}
            </Typography>
          </Box>
        </Box>

        {/* Unlock date */}
        {unlocked && unlockedDate && (
          <Chip
            label={`Unlocked ${unlockedDate}`}
            size="small"
            sx={{
              bgcolor: colors.border,
              color: colors.text,
              fontWeight: 600,
              fontSize: '0.65rem',
              height: 20,
              mb: 1,
            }}
          />
        )}

        {/* Near-miss progress bar */}
        {showProgress && (
          <Box sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.65rem' }}>
                Progress
              </Typography>
              <Typography variant="caption" sx={{ color: '#555', fontSize: '0.65rem', fontWeight: 'bold' }}>
                {progressInfo.current} / {progressInfo.max}
              </Typography>
            </Box>
            <Tooltip title={`${progressInfo.pct}% there — keep going!`} placement="top">
              <LinearProgress
                variant="determinate"
                value={progressInfo.pct}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  bgcolor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    bgcolor:
                      progressInfo.pct >= 80
                        ? '#4caf50'
                        : progressInfo.pct >= 50
                        ? '#ff9800'
                        : '#2196f3',
                    borderRadius: 3,
                  },
                }}
              />
            </Tooltip>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// ─── Summary bar ─────────────────────────────────────────────────────────────

const SummaryBar = ({ total, unlocked }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      mb: 3,
      p: 2,
      bgcolor: '#f5f5f5',
      borderRadius: 2,
      flexWrap: 'wrap',
    }}
  >
    <EmojiEventsIcon sx={{ color: '#ffc107', fontSize: 32 }} />
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1 }}>
        {unlocked} / {total}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Achievements unlocked
      </Typography>
    </Box>
    <Box sx={{ flexGrow: 1, minWidth: 120 }}>
      <LinearProgress
        variant="determinate"
        value={total > 0 ? Math.round((unlocked / total) * 100) : 0}
        sx={{
          height: 10,
          borderRadius: 5,
          bgcolor: '#e0e0e0',
          '& .MuiLinearProgress-bar': { bgcolor: '#58CC02', borderRadius: 5 },
        }}
      />
    </Box>
    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#58CC02' }}>
      {total > 0 ? Math.round((unlocked / total) * 100) : 0}%
    </Typography>
  </Box>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const AchievementsPage = () => {
  const { unlockedAchievements, getAchievementProgress } = useGame();
  const [activeCategory, setActiveCategory] = useState('all');
  const containerRef = useRef(null);
  const filtersBoxRef = useRef(null);
  const achievGridRef = useRef(null);
  const achievCard0Ref = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const log = () => {
      const c  = containerRef.current?.getBoundingClientRect();
      const f  = filtersBoxRef.current?.getBoundingClientRect();
      const ag = achievGridRef.current?.getBoundingClientRect();
      const a0 = achievCard0Ref.current?.getBoundingClientRect();
      const agCS = achievGridRef.current  ? getComputedStyle(achievGridRef.current)  : null;
      const a0CS = achievCard0Ref.current ? getComputedStyle(achievCard0Ref.current) : null;
      console.log('[WIDTH-DEBUG] AchievementsPage');
      console.log('  container:', c?.width?.toFixed(1), '| window:', window.innerWidth);
      console.log('  filtersBox BBox:', f?.width?.toFixed(1),
        'offsetW:', filtersBoxRef.current?.offsetWidth);
      console.log('  achievGrid BBox:', ag?.width?.toFixed(1),
        'offsetW:', achievGridRef.current?.offsetWidth,
        'computed width:', agCS?.width,
        'computed margin:', agCS?.marginLeft, agCS?.marginRight);
      console.log('  card[0] BBox:', a0?.width?.toFixed(1),
        'offsetW:', achievCard0Ref.current?.offsetWidth,
        'computed width:', a0CS?.width,
        'computed padding:', a0CS?.paddingLeft, a0CS?.paddingRight,
        'computed flexBasis:', a0CS?.flexBasis);
      // Walk parent chain
      let el = containerRef.current?.parentElement;
      for (let i = 0; i < 6 && el; i++) {
        const r = el.getBoundingClientRect();
        const cs = getComputedStyle(el);
        console.log(`  parent[${i}] tag=${el.tagName} class="${el.className?.substring?.(0,40)}" w=${r.width.toFixed(1)} overflowX=${cs.overflowX} maxW=${cs.maxWidth}`);
        el = el.parentElement;
      }
    };
    requestAnimationFrame(log);
    window.addEventListener('resize', log);
    return () => window.removeEventListener('resize', log);
  }, []);


  const progressMap = useMemo(() => getAchievementProgress(), [getAchievementProgress]); // eslint-disable-line react-hooks/exhaustive-deps

  const unlockedMap = useMemo(() => {
    const map = {};
    (unlockedAchievements || []).forEach((entry) => {
      map[entry.id] = entry;
    });
    return map;
  }, [unlockedAchievements]);

  const categories = achievementsData.categories;
  const allAchievements = achievementsData.achievements;

  const filtered = useMemo(
    () =>
      activeCategory === 'all'
        ? allAchievements
        : allAchievements.filter((a) => a.category === activeCategory),
    [activeCategory, allAchievements]
  );

  // Sort: unlocked first (by unlock date desc), then locked (with progress first)
  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aUnlocked = !!unlockedMap[a.id];
      const bUnlocked = !!unlockedMap[b.id];
      if (aUnlocked && !bUnlocked) return -1;
      if (!aUnlocked && bUnlocked) return 1;
      if (aUnlocked && bUnlocked) {
        // Both unlocked — sort by unlock date desc
        return (
          new Date(unlockedMap[b.id].unlockedAt || 0) -
          new Date(unlockedMap[a.id].unlockedAt || 0)
        );
      }
      // Both locked — sort by progress pct desc (near-miss first)
      const aPct = progressMap[a.id]?.pct || 0;
      const bPct = progressMap[b.id]?.pct || 0;
      return bPct - aPct;
    });
  }, [filtered, unlockedMap, progressMap]);

  const unlockedCount = (unlockedAchievements || []).length;

  return (
    <Box ref={containerRef} sx={{ px: { xs: 2, sm: 3 }, py: 4, width: '100%', boxSizing: 'border-box' }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ fontWeight: 'bold', color: '#58CC02', display: 'flex', alignItems: 'center', gap: 1 }}
      >
        🏆 Achievements
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Earn achievements by practising, improving, and mastering your voice.
        Locked achievements show a hint — can you crack them all?
      </Typography>

      <SummaryBar total={allAchievements.length} unlocked={unlockedCount} />

      {/* Category filter */}
      <Box ref={filtersBoxRef} sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 1, width: '100%' }}>
        <ToggleButtonGroup
          value={activeCategory}
          exclusive
          onChange={(_, v) => v && setActiveCategory(v)}
          size="small"
          sx={{ display: 'flex', flexWrap: 'wrap', width: '100%', gap: 0.5 }}
        >
          <ToggleButton value="all" sx={{ borderRadius: 2, px: 1.5, fontWeight: 600, flex: '1 1 auto', minWidth: 0 }}>
            All ({allAchievements.length})
          </ToggleButton>
          {categories.map((cat) => {
            const catCount = allAchievements.filter((a) => a.category === cat.id).length;
            const catUnlocked = allAchievements.filter(
              (a) => a.category === cat.id && unlockedMap[a.id]
            ).length;
            return (
              <ToggleButton
                key={cat.id}
                value={cat.id}
                sx={{ borderRadius: 2, px: 1.5, fontWeight: 600, flex: '1 1 auto', minWidth: 0, fontSize: { xs: '0.7rem', sm: '0.8125rem' } }}
              >
                {cat.icon} {cat.label} ({catUnlocked}/{catCount})
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {/* Achievement grid */}
      <Grid ref={achievGridRef} container spacing={{ xs: 1.5, sm: 2 }} alignItems="stretch">
        {sorted.map((achievement, idx) => (
          <Grid size={{ xs: 6, sm: 6, md: 4 }} key={achievement.id} ref={idx === 0 ? achievCard0Ref : undefined}>
            <AchievementCard
              achievement={achievement}
              unlockedEntry={unlockedMap[achievement.id] || null}
              progressInfo={progressMap[achievement.id] || null}
            />
          </Grid>
        ))}
      </Grid>

      {sorted.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="body1" color="text.secondary">
            No achievements in this category yet.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AchievementsPage;
